import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
  };
  noindex?: boolean;
  jsonLd?: object;
}

const SITE_NAME = 'The Bulk Image Generator';
const DEFAULT_IMAGE = 'https://thebulkimagegenerator.com/og-image.png';
const SITE_URL = 'https://thebulkimagegenerator.com';

export const SEO = ({
  title,
  description,
  keywords,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  article,
  noindex = false,
  jsonLd,
}: SEOProps) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = url ? `${SITE_URL}${url}` : SITE_URL;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Article specific */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article?.author && <meta property="article:author" content={article.author} />}
      {article?.section && <meta property="article:section" content={article.section} />}
      
      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-configured JSON-LD schemas
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'The Bulk Image Generator',
  url: 'https://thebulkimagegenerator.com',
  logo: 'https://thebulkimagegenerator.com/logo.png',
  description: 'AI-powered bulk image generation platform using FLUX models',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@aigeneratedbulkimages.com',
    contactType: 'customer service',
  },
  sameAs: [
    'https://twitter.com/bulkimagegen',
    'https://linkedin.com/company/bulk-image-generator',
  ],
};

export const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'The Bulk Image Generator',
  applicationCategory: 'DesignApplication',
  operatingSystem: 'Web',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    ratingCount: '150',
  },
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'The Bulk Image Generator',
  url: 'https://thebulkimagegenerator.com',
  description: 'AI-powered bulk image generation platform using FLUX models',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://thebulkimagegenerator.com/prompts?search={search_term_string}'
    },
    'query-input': 'required name=search_term_string'
  }
};

export const createBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `https://thebulkimagegenerator.com${item.url}`
  }))
});

export const createArticleSchema = (article: {
  title: string;
  description: string;
  author: string;
  datePublished: string;
  image?: string;
  url: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.description,
  author: {
    '@type': 'Person',
    name: article.author,
  },
  publisher: {
    '@type': 'Organization',
    name: 'The Bulk Image Generator',
    logo: {
      '@type': 'ImageObject',
      url: 'https://thebulkimagegenerator.com/logo.png',
    },
  },
  datePublished: article.datePublished,
  image: article.image,
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `https://thebulkimagegenerator.com${article.url}`,
  },
});

export const createFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

// Product/Pricing Schema for rich results
export const productSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'The Bulk Image Generator',
  applicationCategory: 'DesignApplication',
  operatingSystem: 'Web Browser',
  offers: [
    {
      '@type': 'Offer',
      name: 'Free Plan',
      price: '0',
      priceCurrency: 'USD',
      description: '5 images per day, basic models',
    },
    {
      '@type': 'Offer',
      name: 'Pro Plan',
      price: '29',
      priceCurrency: 'USD',
      priceValidUntil: '2026-12-31',
      description: '500 images per month, all models, priority queue',
    },
    {
      '@type': 'Offer',
      name: 'Business Plan',
      price: '99',
      priceCurrency: 'USD',
      description: 'Unlimited images, API access, dedicated support',
    },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '250',
    bestRating: '5',
  },
};

// HowTo Schema for documentation/tutorials
export const createHowToSchema = (steps: { name: string; text: string }[], title: string, description: string, totalTime?: string) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: title,
  description: description,
  totalTime: totalTime || 'PT10M',
  step: steps.map((step) => ({
    '@type': 'HowToStep',
    name: step.name,
    text: step.text,
  })),
});

// Video Schema for tutorials
export const createVideoSchema = (video: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
  contentUrl?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: video.name,
  description: video.description,
  thumbnailUrl: video.thumbnailUrl,
  uploadDate: video.uploadDate,
  duration: video.duration,
  contentUrl: video.contentUrl,
  publisher: {
    '@type': 'Organization',
    name: 'The Bulk Image Generator',
    logo: {
      '@type': 'ImageObject',
      url: 'https://thebulkimagegenerator.com/logo.png',
    },
  },
});

// Local Business Schema for trust signals
export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareCompany',
  name: 'The Bulk Image Generator',
  url: 'https://thebulkimagegenerator.com',
  logo: 'https://thebulkimagegenerator.com/logo.png',
  description: 'AI-powered bulk image generation platform using FLUX models',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Ahmedabad',
    addressRegion: 'Gujarat',
    addressCountry: 'India',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+91-92657-11651',
    contactType: 'customer service',
    email: 'support@aigeneratedbulkimages.com',
    availableLanguage: 'English',
  },
  sameAs: [
    'https://twitter.com/bulkimagegen',
    'https://linkedin.com/company/bulk-image-generator',
  ],
};

export default SEO;
